import { useState } from "react";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import color from "../../Constants/colors";
import { useNavigate } from "react-router-dom";
import { useSkills, useDeleteSkill } from "../../Query/useSkill";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { useTranslation } from "react-i18next";

const SkillsList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("skillForm");
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedSkillId, setSelectedSkillId] = useState<number | null>(null);
  const { mutate: deleteSkill } = useDeleteSkill();
  const { colors: workspace } = useWorkspaceColors();

  const { data, isLoading, isError } = useSkills();

  const handleConfirmationDialogClose = (confirmed: boolean) => {
    setConfirmationDialogOpen(false);
    if (confirmed && selectedSkillId) {
      deleteSkill(selectedSkillId);
    }
  };

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 4, color: color.red700 }}>{t("failedToFetchSkills")}</Box>
    );
  }

  const headerCellSx = {
    color: color.gray600,
    fontWeight: 600,
    fontSize: "0.8rem",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  };

  return (
    <Box sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: color.gray50 }}>
      <Box sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <LightbulbOutlinedIcon
                sx={{ fontSize: 32, color: workspace.primary_dark }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: workspace.primary_dark }}
                >
                  {t("skills")}
                </Typography>
                <Typography variant="caption" sx={{ color: color.gray500 }}>
                  {data?.length ?? 0}
                </Typography>
              </Box>
            </Stack>

            <IconButton
              onClick={() => navigate("/skill/create")}
              sx={{
                bgcolor: workspace.accent,
                color: workspace.primary_dark,
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
                gap: 0.75,
                "&:hover": { bgcolor: workspace.accent, opacity: 0.9 },
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
              >
                {t("createNew")}
              </Typography>
            </IconButton>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
          }}
        >
          <TableContainer
            sx={{
              border: `1px solid ${color.gray200}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: color.gray50 }}>
                  <TableCell sx={{ ...headerCellSx, width: "30%" }}>
                    {t("name")}
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx, width: "50%" }}>
                    {t("description")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ ...headerCellSx, width: "20%" }}
                  >
                    {t("actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data ?? []).map((skill) => (
                  <TableRow
                    key={skill.id}
                    sx={{
                      "&:hover": { bgcolor: color.gray50 },
                      cursor: "pointer",
                    }}
                    onClick={() => navigate(`/skill/edit/${skill.id}`)}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: color.primary_dark }}
                      >
                        {skill.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: color.gray600,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {skill.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title={t("edit")}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/skill/edit/${skill.id}`)}
                            sx={{ color: color.secondary }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("delete")}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedSkillId(skill.id);
                              setConfirmationDialogOpen(true);
                            }}
                            sx={{
                              color: color.gray400,
                              "&:hover": { color: color.red },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {(data ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: "center", py: 6 }}>
                      <Typography sx={{ color: color.gray400 }}>
                        {t("noSkillsYet")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={handleConfirmationDialogClose}
        title={t("deleteSkill")}
        description={t("deleteSkillDescription")}
        content={t("deleteSkillContent")}
        actions={[
          { label: t("no"), onClick: () => handleConfirmationDialogClose(false) },
          {
            label: t("yes"),
            onClick: () => handleConfirmationDialogClose(true),
          },
        ]}
      />
    </Box>
  );
};

export default SkillsList;
